// Jotai atoms and hooks for protocols.

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { protocolService, workflowService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { toast } from '@/components/ui/sonner';
import type { VectorSyncResult } from '@/types';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { CreateProtocolInput, UpdateProtocolInput } from '@/modules/protocols/types';
import type { PatchSEGNodeDto } from '@/types';
import type { AiReviewFocus } from '@/services/workflow.service';

// A protocol is a workflow, so both lists refresh; ['protocols'] covers the by-id query by prefix.
const refreshProtocols = () => {
  invalidateQueries(['protocols']);
  invalidateQueries(['workflows']);
};

const protocolByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['protocols', id],
    queryFn: () => protocolService.getProtocolById(id),
    enabled: !!id,
    staleTime: 30 * 1000
  }));

// Scoped like the query atoms so a create lands on the selected brand, not shared.
const createProtocolMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['protocols', 'create'],
  mutationFn: (data: CreateProtocolInput) => protocolService.createProtocol(data, buScopeParams(get)),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const updateProtocolMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateProtocolInput }) => protocolService.updateProtocol(id, data),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const compileProtocolMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'compile'],
  mutationFn: ({ id, data }: { id: string; data: UpdateProtocolInput }) =>
    protocolService.updateProtocol(id, data, { compile: true }),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const patchProtocolSegNodeMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'patch-seg-node'],
  mutationFn: ({ id, body }: { id: string; body: PatchSEGNodeDto }) => protocolService.patchSegNode(id, body),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const deleteProtocolMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'delete'],
  mutationFn: (id: string) => protocolService.deleteProtocol(id),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const updateProtocolChannelsMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'channels'],
  mutationFn: ({ id, enabledChannels }: { id: string; enabledChannels: string[] }) =>
    protocolService.updateProtocolChannels(id, enabledChannels),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const resyncProtocolMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'resync'],
  mutationFn: (id: string): Promise<VectorSyncResult> => protocolService.resyncProtocol(id),
  onSuccess: (result: VectorSyncResult) => {
    if (result.status === 'synced') toast.success('Vector sync complete');
    else if (result.status === 'skipped') toast.message(`Skipped: ${result.reason ?? 'protocol no longer qualifies'}`);
    else if (result.status === 'failed') toast.error(result.error ?? 'Vector sync failed');
    refreshProtocols();
  },
  onError: () => toast.error('Could not resync the protocol')
}));

const toggleProtocolStatusMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'toggle-status'],
  mutationFn: (id: string) => protocolService.toggleProtocolStatus(id),
  onSuccess: () => {
    refreshProtocols();
  }
}));

// The listing's category filter: the categories protocols are filed under, with counts.
const protocolCategoryTreeQueryAtom = atomWithQuery((get) => ({
  queryKey: ['workflows', 'category-tree', buScopeKey(get)],
  queryFn: () => protocolService.getCategoryTree(buScopeParams(get)),
  staleTime: 30 * 1000
}));

const protocolTextQueryAtom = (id: string, enabled: boolean) =>
  atomWithQuery(() => ({
    queryKey: ['protocols', id, 'text'],
    queryFn: () => protocolService.getProtocolText(id),
    enabled: enabled && !!id,
    staleTime: 0
  }));

const updateProtocolCategoryMutationAtom = atomWithMutation(() => ({
  mutationKey: ['protocols', 'category'],
  mutationFn: ({ id, category, subcategory }: { id: string; category: string; subcategory: string }) =>
    protocolService.updateProtocolCategory(id, category, subcategory),
  onSuccess: () => {
    refreshProtocols();
  }
}));

const autoCategorizeProtocolsMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['protocols', 'auto-categorize'],
  mutationFn: () => protocolService.autoCategorize(buScopeParams(get)),
  onSuccess: ({ scanned, categorized }: { scanned: number; categorized: number }) => {
    // What was left keeps its reason: the ⓘ next to it says why on hover.
    if (scanned === 0) toast.message('Everything already has a category');
    else if (categorized === scanned) toast.success(`Filed ${categorized} of ${scanned} protocols under a category`);
    else toast.message(`Filed ${categorized} of ${scanned} protocols`, { description: 'Hover the ⓘ on the rest to see why.' });
    refreshProtocols();
  },
  onError: () => toast.error('Could not categorize the protocols')
}));

const generateProtocolMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['protocols', 'generate'],
  mutationFn: (prompt: string) => protocolService.generateFromPrompt(prompt, buScopeParams(get)),
  onSuccess: () => {
    refreshProtocols();
  }
}));

// Scoped like generate; it saves nothing, so nothing to refresh.
const aiEditProtocolMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['protocols', 'ai-edit'],
  mutationFn: ({ id, body }: { id: string; body: { doc: Record<string, unknown>; instruction: string; baseEditorDataUpdatedAt: string | null } }) =>
    workflowService.aiEditWorkflow(id, body, buScopeParams(get))
}));

// Read-only: it changes nothing, so nothing to refresh.
const aiReviewProtocolMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['protocols', 'ai-review'],
  mutationFn: ({ id, body }: { id: string; body: { doc: Record<string, unknown>; focus: AiReviewFocus } }) =>
    workflowService.aiReviewWorkflow(id, body, buScopeParams(get))
}));

export function useAiReviewProtocol() {
  const [mutation] = useAtom(aiReviewProtocolMutationAtom);
  return mutation;
}

export function useAiEditProtocol() {
  const [mutation] = useAtom(aiEditProtocolMutationAtom);
  return mutation;
}

export function useProtocolCategoryTree() {
  const [query] = useAtom(protocolCategoryTreeQueryAtom);
  return query;
}

/** The protocol's text format, fetched only while something shows it. */
export function useProtocolText(id: string, enabled: boolean) {
  const atom = useMemo(() => protocolTextQueryAtom(id, enabled), [id, enabled]);
  const [query] = useAtom(atom);
  return query;
}

export function useUpdateProtocolCategory() {
  const [mutation] = useAtom(updateProtocolCategoryMutationAtom);
  return mutation;
}

export function useAutoCategorizeProtocols() {
  const [mutation] = useAtom(autoCategorizeProtocolsMutationAtom);
  return mutation;
}

export function useGenerateProtocol() {
  const [mutation] = useAtom(generateProtocolMutationAtom);
  return mutation;
}

export function useProtocolById(id: string) {
  const atom = useMemo(() => protocolByIdQueryAtom(id), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useCreateProtocol() {
  const [mutation] = useAtom(createProtocolMutationAtom);
  return mutation;
}

export function useUpdateProtocol() {
  const [mutation] = useAtom(updateProtocolMutationAtom);
  return mutation;
}

export function useCompileProtocol() {
  const [mutation] = useAtom(compileProtocolMutationAtom);
  return mutation;
}

export function usePatchProtocolSegNode() {
  const [mutation] = useAtom(patchProtocolSegNodeMutationAtom);
  return mutation;
}

export function useDeleteProtocol() {
  const [mutation] = useAtom(deleteProtocolMutationAtom);
  return mutation;
}

export function useUpdateProtocolChannels() {
  const [mutation] = useAtom(updateProtocolChannelsMutationAtom);
  return mutation;
}

export function useToggleProtocolStatus() {
  const [mutation] = useAtom(toggleProtocolStatusMutationAtom);
  return mutation;
}

export function useResyncProtocol() {
  const [mutation] = useAtom(resyncProtocolMutationAtom);
  return mutation;
}
